import type { StaticEntityType } from "@config";
import { Set } from "@library-containers/layers";
import { useIsMobile } from "@library/shared";
import { useMemo } from "react";
import { TabsAutoplay, TabsProvider } from "../providers";
import type { TabTitleType, TabsPayload } from "../types";
import {
  defaultTabId,
  getItemsInRow,
  getPlaceholders,
  getTabsOptions,
  getTitlePositionAttributes,
} from "../utils";
import { SliderWrapper } from "./SliderWrapper";
import { TabsView } from "./Tabs.view";
import { Title } from "./Title";
import { Titles } from "./Titles";
import { Wrapper } from "./Wrapper";

export const TabsLibrary = ({ entity }: { entity: StaticEntityType }) => {
  const { settings, children, id } = entity as StaticEntityType<TabsPayload>;
  const titles = getTabsOptions(entity) as unknown as TabTitleType[];
  const isMobile = useIsMobile();
  const dynamicVariable = entity.settings.dynamicVariableDefaultActiveTabIndex;
  const initialTabId = useMemo(
    () => defaultTabId(titles, dynamicVariable),
    [titles, dynamicVariable],
  );

  const itemsInRow = getItemsInRow({ settings, length: titles.length });
  const { placeholdersCount, maxItems } = getPlaceholders({
    length: titles.length,
    itemsInRow,
    design: settings.tabsDesign,
  });
  const placeholders = Array.from({ length: placeholdersCount }, (_, i) => i);

  return (
    <TabsProvider id={initialTabId}>
      <TabsView entity={entity}>
        <TabsAutoplay
          settings={settings}
          titles={titles}
          id={id}
          isMobile={isMobile}
        >
          {!isMobile && (
            <Titles entitiesLength={titles.length} settings={settings}>
              {titles.map((title, index) => (
                <Title
                  settings={settings}
                  title={title}
                  key={title.id}
                  attributes={{
                    ...getTitlePositionAttributes({
                      index,
                      tabsInRow: itemsInRow,
                      total: titles.length + placeholdersCount,
                      max: maxItems,
                      design: settings.tabsDesign,
                    }),
                    "data-tab-id": title.id,
                  }}
                />
              ))}
              {placeholders.map((index) => (
                <div
                  key={index}
                  className="tab-placeholder-gh08QFXRG3"
                  {...getTitlePositionAttributes({
                    index: titles.length + index,
                    tabsInRow: itemsInRow,
                    total: titles.length + placeholdersCount,
                    max: maxItems,
                    design: settings.tabsDesign,
                  })}
                ></div>
              ))}
            </Titles>
          )}
          <div className="tw-w-full">
            <SliderWrapper id={id} settings={settings} titles={titles}>
              {children.map((child: StaticEntityType, index: number) => (
                <Wrapper
                  id={child.id}
                  isMobile={isMobile}
                  key={child.id}
                  title={titles[index]}
                  settings={settings}
                >
                  <Set
                    entity={{
                      ...child,
                      layers: {
                        ...(entity.layers || {}),
                        combo: { settings, index, id },
                      },
                    }}
                  />
                </Wrapper>
              ))}
            </SliderWrapper>
          </div>
        </TabsAutoplay>
      </TabsView>
    </TabsProvider>
  );
};
